import { useCallback, useMemo, useState } from 'react';
import './figma.css';
import { API_BASE_URL } from '../../lib/api';

type Field = {
  name: 'fullName' | 'email';
  label: string;
  type: 'text' | 'email';
  placeholder: string;
  required?: boolean;
};

const fields: Field[] = [
  {
    name: 'fullName',
    label: 'Nom et prénom',
    type: 'text',
    placeholder: 'Jean Dupont',
    required: true,
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'jean.dupont@example.com',
    required: true,
  },
];

type FormState = Record<'fullName' | 'email', string>;
type ErrorState = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
  fullName: '',
  email: '',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (state: FormState): ErrorState => {
  const errors: ErrorState = {};

  if (!state.fullName.trim()) {
    errors.fullName = 'Le nom est obligatoire';
  }

  if (!state.email.trim()) {
    errors.email = 'L\'email est obligatoire';
  } else if (!emailRegex.test(state.email)) {
    errors.email = 'Email invalide';
  }

  return errors;
};

export type SessionPayload = {
  sessionId: string;
  score: number;
  user: {
    id: number;
    fullName: string;
    email: string;
  };
};

interface AuthFormProps {
  onClose: () => void;
  onSuccess: (payload: SessionPayload) => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<ErrorState>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const isValid = useMemo(() => Object.keys(validate(form)).length === 0, [form]);

  const handleChange = useCallback((name: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlur = useCallback((name: keyof FormState) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(form));
  }, [form]);

  const resetForm = useCallback(() => {
    setForm(initialState);
    setErrors({});
    setTouched({});
    setServerError(null);
  }, []);

  const handleInternalClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validationErrors = validate(form);
      setErrors(validationErrors);
      setTouched({ fullName: true, email: true });

      if (Object.keys(validationErrors).length === 0) {
        try {
          setSubmitting(true);
          setServerError(null);
          const response = await fetch(`${API_BASE_URL}/auth/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fullName: form.fullName.trim(),
              email: form.email.trim(),
            }),
          });

          if (!response.ok) {
            const errorBody = await response.json().catch(() => undefined);
            const message = errorBody?.message ?? 'Une erreur est survenue';
            throw new Error(Array.isArray(message) ? message.join(', ') : message);
          }

          const payload = (await response.json()) as SessionPayload;
          console.log('Session created', payload);
          onSuccess(payload);
          resetForm();
        } catch (error) {
          setServerError(error instanceof Error ? error.message : 'Une erreur est survenue');
        } finally {
          setSubmitting(false);
        }
      }
    },
    [form, onSuccess, resetForm]
  );

  return (
    <div className="auth-form" role="dialog" aria-modal="true" aria-labelledby="auth-form-title">
      <form className="auth-form__content" onSubmit={handleSubmit} noValidate>
        <div className="auth-form__header">
          <h2 id="auth-form-title">Authentification</h2>
          <button
            type="button"
            className="auth-form__close"
            onClick={handleInternalClose}
            aria-label="Fermer le formulaire"
          >
            ×
          </button>
        </div>

        <div className="auth-form__fields">
          {fields.map((field) => {
            const error = touched[field.name] ? errors[field.name] : undefined;
            return (
              <label key={field.name} className="auth-form__field">
                <span className="auth-form__label">
                  {field.label}
                  {field.required ? <span aria-hidden="true">*</span> : null}
                </span>
                <input
                  className={`auth-form__input${error ? ' auth-form__input--error' : ''}`}
                  type={field.type}
                  name={field.name}
                  value={form[field.name]}
                  onChange={(event) => handleChange(field.name, event.target.value)}
                  onBlur={() => handleBlur(field.name)}
                  placeholder={field.placeholder}
                  required={field.required}
                  autoComplete={field.name === 'fullName' ? 'name' : 'email'}
                />
                {error ? <span className="auth-form__error">{error}</span> : null}
              </label>
            );
          })}
        </div>
        {serverError ? <div className="auth-form__server-error">{serverError}</div> : null}
        <button className="auth-form__submit" type="submit" disabled={!isValid || submitting}>
          {submitting ? 'En cours…' : 'Valider'}
        </button>
      </form>
    </div>
  );
};

export default AuthForm;
